import {FileText, Network, Search, StickyNote, X} from 'lucide-react';
import {FormEvent, KeyboardEvent, useEffect, useId, useState} from 'react';
import {useNavigate} from '../router';
import {apiRequest} from '../api';

type SearchResult = {
  id: string;
  kind: 'opportunity' | 'relationship' | 'note' | 'document';
  title: string;
  subtitle: string;
  snippet: string;
  href: string;
  updatedAt: string;
};

const resultIcons = {
  opportunity: Search,
  relationship: Network,
  note: StickyNote,
  document: FileText,
} as const;

export const GlobalSearch = ({onClose}: {onClose: () => void}) => {
  const navigate = useNavigate();
  const listId = useId();
  const [query, setQuery] = useState('');
  const [items, setItems] = useState<SearchResult[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [state, setState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [error, setError] = useState('');

  useEffect(() => {
    const closeOnEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [onClose]);

  useEffect(() => {
    const needle = query.trim();
    if (needle.length < 2) {
      setItems([]);
      setActiveIndex(0);
      setState('idle');
      setError('');
      return;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      setState('loading');
      setError('');
      void apiRequest<{items: SearchResult[]}>(
        '/search?q=' + encodeURIComponent(needle) + '&limit=24',
        {signal: controller.signal},
      ).then((result) => {
        setItems(result.items);
        setActiveIndex(0);
        setState('ready');
      }).catch((reason: unknown) => {
        if (controller.signal.aborted) return;
        setItems([]);
        setState('error');
        setError(reason instanceof Error ? reason.message : 'Search could not be completed.');
      });
    }, 180);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [query]);

  const choose = (item?: SearchResult) => {
    if (!item) return;
    navigate(item.href);
    onClose();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!items.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % items.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => (index - 1 + items.length) % items.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      choose(items[activeIndex]);
    }
  };

  return (
    <form
      className="pd-global-search"
      role="search"
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        choose(items[activeIndex]);
      }}
    >
      <div className="pd-global-search-input">
        <Search size={17} aria-hidden="true" />
        <label className="pd-visually-hidden" htmlFor="global-search">
          Search opportunities, people, notes, and documents
        </label>
        <input
          id="global-search"
          autoFocus
          autoComplete="off"
          value={query}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={items.length > 0}
          aria-controls={listId}
          aria-activedescendant={items[activeIndex] ? `${listId}-${activeIndex}` : undefined}
          placeholder="Search opportunities, people, notes, and documents…"
          onChange={(event) => setQuery(event.currentTarget.value)}
          onKeyDown={onKeyDown}
        />
        <button type="button" className="pd-search-close" onClick={onClose} aria-label="Close search">
          <X size={16} aria-hidden="true" />
        </button>
        <kbd>Esc</kbd>
      </div>
      <div className="pd-global-search-results" id={listId} role="listbox" aria-label="Search results">
        {state === 'idle' ? <p>Type at least two characters to search your private workspace.</p> : null}
        {state === 'loading' ? <p role="status">Searching your workspace…</p> : null}
        {state === 'error' ? <p role="alert">{error}</p> : null}
        {state === 'ready' && items.length === 0 ? <p>No matching records.</p> : null}
        {items.map((item, index) => {
          const Icon = resultIcons[item.kind];
          return (
            <button
              key={item.kind + ':' + item.id}
              id={`${listId}-${index}`}
              type="button"
              role="option"
              aria-selected={index === activeIndex}
              className={index === activeIndex ? 'is-active' : ''}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => choose(item)}
            >
              <span className="pd-search-result-icon" aria-hidden="true"><Icon size={16} /></span>
              <span>
                <strong>{item.title}</strong>
                <small>{item.kind.replaceAll('_', ' ')}{item.subtitle ? ' · ' + item.subtitle : ''}</small>
                {item.snippet ? <em>{item.snippet}</em> : null}
              </span>
            </button>
          );
        })}
      </div>
    </form>
  );
};
